/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import type { Dispatch } from '@reduxjs/toolkit';
import Apis from '@/app/index';
import {
  RTCConfig,
  SceneConfig,
  updateRTCConfig,
  updateScene,
  updateSceneConfig,
} from '@/store/slices/room';

type ScenePayload = {
  rtc: RTCConfig;
  scene: SceneConfig;
};

export const refreshSceneRuntime = async (
  dispatch: Dispatch,
  preferredSceneId?: string,
  // Client-side companion overlay currently in redux. The server scene config
  // never carries `live2dModelUrl` / `portraitFrames` / `avatarBgVideoUrl`
  // (they live in the companion store and are merged in on the client), so
  // re-fetching scenes on join would otherwise wipe them and drop the Live2D
  // model / lip-sync frames / looping video background.
  preserveSceneConfigMap?: Record<string, SceneConfig>
) => {
  const { scenes }: { scenes: ScenePayload[] } = await Apis.Basic.getScenes();
  if (!scenes.length) {
    return {
      scenes,
      activeSceneId: '',
    };
  }

  const sceneConfigMap = scenes.reduce<Record<string, SceneConfig>>((prev, cur) => {
    const preserved = preserveSceneConfigMap?.[cur.scene.id];
    prev[cur.scene.id] = preserved
      ? {
          ...cur.scene,
          live2dModelUrl: preserved.live2dModelUrl || cur.scene.live2dModelUrl,
          portraitFrames: preserved.portraitFrames || cur.scene.portraitFrames,
          avatarBgVideoUrl: preserved.avatarBgVideoUrl || cur.scene.avatarBgVideoUrl,
        }
      : cur.scene;
    return prev;
  }, {});
  const rtcConfigMap = scenes.reduce<Record<string, RTCConfig>>((prev, cur) => {
    prev[cur.scene.id] = cur.rtc;
    return prev;
  }, {});
  const activeSceneId = preferredSceneId && sceneConfigMap[preferredSceneId]
    ? preferredSceneId
    : scenes[0].scene.id;

  dispatch(updateScene(activeSceneId));
  dispatch(updateSceneConfig(sceneConfigMap));
  dispatch(updateRTCConfig(rtcConfigMap));

  return {
    scenes,
    activeSceneId,
  };
};
